import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import {
  MdArrowBack, MdPerson, MdPhone, MdBadge, MdLocationOn,
  MdCalendarToday, MdPrint, MdSave, MdLocalHospital, MdHistory,
  MdScience, MdMonitorHeart, MdCheckCircle,
} from "react-icons/md";
import doctorService from "../../Services/doctorService";
import logo1 from "../../assets/logo2.png";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(str);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function fmtDateTime(d) {
  const dd  = String(d.getDate()).padStart(2, "0");
  const mm  = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh  = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss  = String(d.getSeconds()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
}

export default function PatientDetail() {
  const { sale_id } = useParams();
  const navigate    = useNavigate();
  const doctor      = JSON.parse(Cookies.get("doctor") || "{}");
  const printRef    = useRef();

  const [detail, setDetail]             = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [historyOpen, setHistoryOpen]   = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [radiologyExams, setRadiologyExams] = useState([]);
  const [selectedLabTestIds, setSelectedLabTestIds] = useState([]);
  const [selectedRadiologyExamIds, setSelectedRadiologyExamIds] = useState([]);
  const [testCatalogLoading, setTestCatalogLoading] = useState(true);

  const [description, setDescription]   = useState("");
  const [history, setHistory]           = useState("");
  const [advice, setAdvice]             = useState("");
  const [diagnose, setDiagnose]         = useState("");

  useEffect(() => {
    const loadTestCatalogs = async () => {
      const [labResult, radiologyResult] = await Promise.allSettled([
        doctorService.getLabTests(),
        doctorService.getRadiologyExams(),
      ]);

      if (labResult.status === "fulfilled") {
        setLabTests(labResult.value.tests || []);
      } else {
        console.error(labResult.reason);
        toast.error("Failed to load lab tests");
      }

      if (radiologyResult.status === "fulfilled") {
        setRadiologyExams(radiologyResult.value.exams || []);
      } else {
        console.error(radiologyResult.reason);
        toast.error("Failed to load radiology exams");
      }
      setTestCatalogLoading(false);
    };
    loadTestCatalogs();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [det, prx] = await Promise.all([
          doctorService.getPatientDetail(sale_id, doctor.id),
          doctorService.getPrescription(sale_id),
        ]);
        setDetail(det.detail || null);
        const p = prx.prescription || null;
        setPrescription(p);
        if (p) {
          setDescription(p.description || "");
          setHistory(p.history || "");
          setAdvice(p.advice || "");
          setDiagnose(p.Diagnose || "");
          setSelectedLabTestIds(
            (p.prescription_tests || [])
              .filter((test) => test.test_type === "lab")
              .map((test) => Number(test.test_id))
          );
          setSelectedRadiologyExamIds(
            (p.prescription_tests || [])
              .filter((test) => test.test_type === "radiology")
              .map((test) => Number(test.test_id))
          );
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load patient details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sale_id, doctor.id]);

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error("Please enter prescription / medicines");
      return;
    }
    setSaving(true);
    try {
      const res = await doctorService.savePrescription({
        sale_id:     Number(sale_id),
        doctor_id:   doctor.id,
        patient_id:  detail.patient_id,
        description: description.trim(),
        history:     history.trim(),
        advice:      advice.trim(),
        diagnose:    diagnose.trim(),
        prescription_tests: {
          lab_test_ids: selectedLabTestIds,
          radiology_exam_ids: selectedRadiologyExamIds,
        },
      });
      toast.success("Prescription saved successfully");
      setPrescription({
        id:          res?.prescription_id,
        description: description.trim(),
        history:     history.trim(),
        advice:      advice.trim(),
        Diagnose:    diagnose.trim(),
        prescription_tests: [
          ...labTests
            .filter((test) => selectedLabTestIds.includes(Number(test.test_id)))
            .map((test) => ({ test_type: "lab", test_id: Number(test.test_id), test_name: test.test_name })),
          ...radiologyExams
            .filter((exam) => selectedRadiologyExamIds.includes(Number(exam.exam_id)))
            .map((exam) => ({ test_type: "radiology", test_id: Number(exam.exam_id), test_name: exam.exam_name })),
        ],
      });
    } catch (e) {
      toast.error(e?.message || "Failed to save prescription");
    } finally {
      setSaving(false);
    }
  };

  const handleViewHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }

    setHistoryOpen(true);
    setHistoryLoading(true);
    setPrescriptionHistory([]);

    try {
      const response = await doctorService.getAllPatients(doctor.id);
      const patientId = String(detail.patient_id);
      const visitsBySaleId = new Map();
      (response.patients || [])
        .filter((visit) => String(visit.patient_id) === patientId && visit.sale_id != null)
        .forEach((visit) => {
          const key = String(visit.sale_id);
          visitsBySaleId.set(key, { ...visitsBySaleId.get(key), ...visit });
        });
      const visits = [...visitsBySaleId.values()];

      // Include this visit even if it is missing from the all-patients response.
      const currentSaleId = String(sale_id);
      if (!visits.some((visit) => String(visit.sale_id) === currentSaleId)) {
        visits.push({
          sale_id: Number(sale_id),
          patient_id: detail.patient_id,
          checkup_date: detail.checkup_date,
          sale_unique_id: detail.sale_unique_id,
          prescription_id: prescription?.id || detail.prescription_id,
        });
      }

      const savedVisits = visits.filter((visit) =>
        visit.prescription_id ||
        (String(visit.sale_id) === currentSaleId && prescription)
      );
      const results = await Promise.allSettled(savedVisits.map(async (visit) => {
        const isCurrentVisit = String(visit.sale_id) === currentSaleId;
        const savedPrescription = isCurrentVisit && prescription
          ? prescription
          : (await doctorService.getPrescription(visit.sale_id)).prescription;

        return savedPrescription ? { visit, prescription: savedPrescription } : null;
      }));

      const entries = results
        .filter((result) => result.status === "fulfilled" && result.value)
        .map((result) => result.value)
        .sort((a, b) => new Date(b.visit.checkup_date || 0) - new Date(a.visit.checkup_date || 0));

      setPrescriptionHistory(entries);
      if (results.length > 0 && entries.length === 0 && results.every((result) => result.status === "rejected")) {
        toast.error("Failed to load prescription history");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load prescription history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;

    const existing = document.getElementById("prescription-print-frame");
    if (existing) existing.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "prescription-print-frame";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient Notes - ${detail?.full_name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Times New Roman', Georgia, serif; color: #1a1a1a; padding: 0; }
          .receipt { max-width: 780px; margin: 0 auto; padding: 30px 40px; }
          .logo-box img { width: 200px; height: 200px; object-fit: contain; display: block; }

          /* Letterhead */
          .letterhead { display: flex; align-items: center; justify-content: center; gap: 18px; padding-bottom: 6px; margin-bottom: 4px; position: relative; }
          .logo-box { width: 62px; height: 62px; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #999; text-align: center; flex-shrink: 0; font-family: Arial, sans-serif; position: absolute; right: 0; top: 0; }
          .letterhead-text { text-align: center; }
          .letterhead-text h1 { font-size: 24px; letter-spacing: 0.02em; color: #111; }
          .letterhead-text p { font-size: 11px; color: #444; margin-top: 3px; font-family: Arial, sans-serif; }
          .title-line { text-align: center; font-size: 15px; font-weight: bold; letter-spacing: 0.05em; margin: 10px 0 16px; font-family: Arial, sans-serif; border-bottom: 1px solid #333; padding-bottom: 10px; }

          /* Patient info grid */
          .info-table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 13px; margin-bottom: 10px; }
          .info-table td { padding: 3px 4px; vertical-align: top; }
          .info-table .label { width: 120px; font-weight: bold; color: #222; white-space: nowrap; }
          .info-table .colon { width: 12px; color: #222; }
          .info-table .value { color: #111; }
          .info-table .right-label { width: 90px; font-weight: bold; color: #222; white-space: nowrap; }

          /* Written By / Signed On row */
          .meta-table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 13px; margin: 14px 0 10px; border-top: 1px solid #ccc; padding-top: 10px; }
          .meta-table td { padding: 3px 4px; vertical-align: top; }
          .meta-table .label { font-weight: bold; color: #222; white-space: nowrap; width: 100px; }
          .meta-table .colon { width: 12px; }

          /* Two-column body: History / Advice on the left, Diagnose / Prescription on the right */
          .body-grid { display: grid; grid-template-columns: 180px 1fr; column-gap: 24px; margin-bottom: 16px; border-top: 1px solid #ccc; padding-top: 14px; min-height: 380px; }
          .left-col { border-right: 1px solid #ccc; padding-right: 20px; display: flex; flex-direction: column; }
          .right-col { display: flex; flex-direction: column; }
          .section-label { font-family: Arial, sans-serif; font-size: 13px; font-weight: bold; color: #1a2e4a; margin-bottom: 8px; }
          .history-block { min-height: 220px; margin-bottom: 20px; }
          .advice-block { flex: 1; min-height: 360px; padding-bottom: 96px; }
          .right-section { border-bottom: 1px solid #ccc; padding-bottom: 12px; margin-bottom: 12px; }
          .right-section:last-child { border-bottom: none; margin-bottom: 0; }
          .diagnose-block { min-height: 140px; }
          .prescription-block { min-height: 390px; padding-bottom: 96px; }
          .field-content {
            font-size: 13px;
            line-height: 1.7;
            white-space: pre-wrap;
            font-family: Arial, sans-serif;
          }

          /* Confirmation line */
          .confirm-text { font-size: 12px; color: #222; margin-top: 20px; font-family: Arial, sans-serif; line-height: 1.6; border-top: 1px solid #ccc; padding-top: 14px; }

          /* Footer */
          .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; font-family: Arial, sans-serif; }
          .footer .printed-note { font-size: 10.5px; color: #999; }

@media print { 
  body { -webkit-print-color-adjust: exact; } 
  @page { size: auto; margin: 0mm; }
}        </style>
      </head>
      <body>${printContent}</body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 300);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <p className="text-gray-400 text-sm">Loading patient details...</p>
    </div>
  );

  if (!detail) return (
    <div className="flex flex-col items-center justify-center py-24">
      <p className="text-gray-400 text-sm mb-4">Patient not found</p>
      <button onClick={() => navigate(-1)} className="text-blue-600 text-sm flex items-center gap-1">
        <MdArrowBack size={16} /> Go back
      </button>
    </div>
  );

  const now = new Date();
  const alreadySaved = !!prescription;
  const prescriptionId = prescription?.id || detail?.prescription_id || null;

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* LEFT */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden p-5">
            <div className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-10 rounded-xl bg-[#215187]/50 flex items-center justify-center">
                  <MdPerson size={22} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#215187]">{detail.full_name}</p>
                  <p className="text-gray-400 text-xs">Patient</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3 border-t border-gray-100">
              <InfoRow icon={<MdPhone size={15} />}         label="Contact"      value={detail.contact_number || "—"} />
              <InfoRow icon={<MdBadge size={15} />}         label="CNIC"         value={detail.cnic || "—"} />
              <InfoRow icon={<MdLocationOn size={15} />}    label="Address"      value={detail.address || "—"} />
              <InfoRow icon={<MdCalendarToday size={15} />} label="Checkup Date" value={fmtDate(detail.checkup_date)} />
              <InfoRow icon={<MdBadge size={15} />}         label="Prescription No."      value={detail.sale_unique_id || "—"} />
              <button
                onClick={handleViewHistory}
                className="w-full mt-2 flex items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 transition-colors hover:border-blue-200 hover:bg-blue-100"
              >
                <MdHistory size={17} /> {historyOpen ? "Hide History" : "View History"}
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Doctor & Fee Details</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <MdLocalHospital size={18} className="text-blue-700" />
              </div>
              <div>
                <p className="text-gray-800 font-medium text-sm">{detail.doctor_name}</p>
                <p className="text-gray-400 text-xs">{detail.specialization}</p>
                {detail.pmdc_no && <p className="text-gray-400 text-xs">PMDC: {detail.pmdc_no}</p>}
              </div>
            </div>
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <FeeRow label="Consultation Fee" value={`Rs. ${detail.fee}`} />
              {detail.doctor_commission != null && <FeeRow label="Doctor Commission" value={`Rs. ${detail.doctor_commission}`} />}
              {detail.hospital_tax != null && <FeeRow label="Hospital Tax" value={`Rs. ${detail.hospital_tax}`} />}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <p className="text-gray-800 font-medium text-sm">Prescription</p>
                {/* {prescriptionId && (
                  <span className="text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    #{prescriptionId}
                  </span>
                )} */}
              </div>
              {alreadySaved && (
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 text-xs bg-[#1a2e4a] hover:bg-[#1e3a5f] text-white px-3 py-1.5 rounded-lg transition-all"
                >
                  <MdPrint size={14} /> Print Receipt
                </button>
              )}
            </div>

            <div className="p-5 space-y-4">
              <PrescriptionField label="Advice" value={advice} onChange={setAdvice} locked={alreadySaved} placeholder="Doctor's advice..." rows={3} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    title: "Lab tests",
                    subtitle: "Choose investigations for this visit",
                    items: labTests,
                    id: "test_id",
                    name: "test_name",
                    selected: selectedLabTestIds,
                    setSelected: setSelectedLabTestIds,
                    icon: MdScience,
                    accent: "blue",
                    loading: "Loading lab tests...",
                    empty: "No active lab tests available.",
                  },
                  {
                    title: "Radiology exams",
                    subtitle: "Choose imaging for this visit",
                    items: radiologyExams,
                    id: "exam_id",
                    name: "exam_name",
                    selected: selectedRadiologyExamIds,
                    setSelected: setSelectedRadiologyExamIds,
                    icon: MdMonitorHeart,
                    accent: "violet",
                    loading: "Loading radiology exams...",
                    empty: "No active radiology exams available.",
                  },
                ].map((section) => {
                  const Icon = section.icon;
                  const tone = section.accent === "blue"
                    ? { icon: "bg-blue-50 text-blue-700", selected: "border-blue-200 bg-blue-50/70", check: "accent-blue-600", count: "bg-blue-100 text-blue-700" }
                    : { icon: "bg-violet-50 text-violet-700", selected: "border-violet-200 bg-violet-50/70", check: "accent-violet-600", count: "bg-violet-100 text-violet-700" };

                  return (
                    <section key={section.title} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/70 px-3.5 py-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.icon}`}>
                            <Icon size={19} />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-gray-800">{section.title}</p>
                            <p className="mt-0.5 text-xs text-gray-500">{section.subtitle}</p>
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${tone.count}`}>
                          {section.selected.length} selected
                        </span>
                      </div>
                      <div className="max-h-44 min-h-[76px] overflow-y-auto p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {testCatalogLoading ? (
                          <p className="px-2 py-4 text-center text-xs text-gray-400">{section.loading}</p>
                        ) : section.items.length === 0 ? (
                          <p className="px-2 py-4 text-center text-xs text-gray-400">{section.empty}</p>
                        ) : (
                          <div className="space-y-1">
                            {section.items.map((item) => {
                              const id = Number(item[section.id]);
                              const checked = section.selected.includes(id);
                              return (
                                <label key={id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-2.5 py-2 text-[13px] transition-colors ${checked ? tone.selected : "border-transparent hover:border-gray-100 hover:bg-gray-50"} ${alreadySaved ? "cursor-default" : ""}`}>
                                  <input
                                    type="checkbox"
                                    disabled={alreadySaved}
                                    checked={checked}
                                    className={`h-4 w-4 shrink-0 rounded border-gray-300 ${tone.check} focus:ring-2 focus:ring-offset-1`}
                                    onChange={() => section.setSelected((selected) =>
                                      selected.includes(id)
                                        ? selected.filter((selectedId) => selectedId !== id)
                                        : [...selected, id]
                                    )}
                                  />
                                  <span className={`min-w-0 flex-1 truncate ${checked ? "font-medium text-gray-800" : "text-gray-600"}`}>{item[section.name]}</span>
                                  {checked && <MdCheckCircle size={15} className="shrink-0 text-green-600" aria-label="Selected" />}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
              <PrescriptionField label="Prescription / Medicines *" value={description} onChange={setDescription} locked={alreadySaved} placeholder="Write medicines, dosage, instructions..." rows={4} />
              <PrescriptionField label="Diagnose" value={diagnose} onChange={setDiagnose} locked={alreadySaved} placeholder="Diagnosis..." rows={3} />
              <PrescriptionField label="Pre-Medical History" value={history} onChange={setHistory} locked={alreadySaved} placeholder="Patient history..." rows={3} />

              {!alreadySaved && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-gray-400">Once saved, prescription cannot be edited.</p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all disabled:opacity-60 font-medium"
                  >
                    <MdSave size={14} />
                    {saving ? "Saving..." : "Save Prescription"}
                  </button>
                </div>
              )}

              {alreadySaved && (
                <p className="text-xs text-green-600 font-medium text-center bg-green-50 py-2 rounded-lg">
                  ✓ Prescription saved — use Print Receipt to print
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {historyOpen && (
        <section className="mt-5 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="font-semibold text-gray-800">Prescription History</h2>
            <p className="mt-1 text-xs text-gray-400">
              {detail.full_name} · Patient ID: {detail.patient_id}
            </p>
          </div>

          <div className="p-5">
            {historyLoading ? (
              <p className="py-8 text-center text-sm text-gray-400">Loading prescription history...</p>
            ) : prescriptionHistory.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No saved prescriptions found for this patient.</p>
            ) : (
              <div className="space-y-4">
                {prescriptionHistory.map(({ visit, prescription: savedPrescription }) => (
                  <article key={visit.sale_id} className="rounded-lg border border-gray-100 p-4">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
                      <p className="text-sm font-semibold text-gray-800">{fmtDate(visit.checkup_date)}</p>
                      <p className="text-xs text-gray-400">
                        Prescription No. {visit.sale_unique_id || visit.sale_id}
                      </p>
                    </div>
                    <HistoryField label="Prescription / Medicines" value={savedPrescription.description} />
                    <HistoryField label="Diagnose" value={savedPrescription.Diagnose || savedPrescription.diagnose} />
                    <HistoryField label="Advice" value={savedPrescription.advice} />
                    <HistoryField
                      label="Recommended Tests"
                      value={(savedPrescription.prescription_tests || [])
                        .map((test) => `${test.test_type === "lab" ? "Lab" : "Radiology"}: ${test.test_name}`)
                        .join(", ") || "—"}
                    />
                    <HistoryField label="Pre-Medical History" value={savedPrescription.history} />
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <div className="hidden">
        <div ref={printRef}>
          <div className="receipt">
            <div className="letterhead">
              <div className="letterhead-text">
                <h1>HOSPITAL MANAGEMENT SYSTEM</h1>
                <p>Pakistan, Lahore. &nbsp;|&nbsp; Phone: +92-XX-XXXXXXX &nbsp;|&nbsp; Fax: +92-XX-XXXXXXX</p>
                <p>Email: hms@gmail.com &nbsp;|&nbsp; Website: https://demo.hms-pk.com/</p>
              </div>
              <div className="logo-box"><img src={logo1} alt="" /></div>
            </div>
            <div className="title-line">PATIENT NOTES</div>

            <table className="info-table">
              <tbody>
                <tr>
                  <td className="label">Patient Record #</td>
                  <td className="colon">:</td>
                  <td className="value" colSpan={3}>{detail.patient_id ? String(detail.patient_id).padStart(11, "0") : "—"}</td>
                </tr>
                <tr>
                  <td className="label">Prescription No.</td>
                  <td className="colon">:</td>
                  <td className="value" colSpan={3}>{detail.sale_unique_id || "—"}</td>
                </tr>
                <tr>
                  <td className="label">Name</td>
                  <td className="colon">:</td>
                  <td className="value" style={{ width: "38%" }}>{detail.full_name}</td>
                  <td className="right-label">Age/Gender</td>
                  <td className="colon">:</td>
                  <td className="value">{detail.age ? `${detail.age} ` : ""}{detail.gender || "—"}</td>
                </tr>
                <tr>
                  <td className="label">Address</td>
                  <td className="colon">:</td>
                  <td className="value">{detail.address || "—"}</td>
                  <td className="right-label">Phone</td>
                  <td className="colon">:</td>
                  <td className="value">{detail.contact_number || "—"}</td>
                </tr>
              </tbody>
            </table>

            <table className="meta-table">
              <tbody>
                <tr>
                  <td className="label">Written By</td>
                  <td className="colon">:</td>
                  <td className="value">{detail.doctor_name} {detail.pmdc_no ? `(${detail.pmdc_no})` : ""}</td>
                  <td className="label" style={{ textAlign: "right" }}>Signed On</td>
                  <td className="colon">:</td>
                  <td className="value">{fmtDateTime(now)}</td>
                </tr>
              </tbody>
            </table>

            <div className="body-grid">
              <div className="left-col">
                <div className="history-block">
                  <div className="section-label">History</div>
                  <div className="field-content">{history || "—"}</div>
                </div>
                <div className="advice-block">
                  <div className="section-label">Advice</div>
                  <div className="field-content">
                    {advice || "—"}
                    {(prescription?.prescription_tests || []).length > 0 && (
                      <div className="mt-2">
                        {["lab", "radiology"].map((type) => {
                          const tests = prescription.prescription_tests
                            .filter((test) => test.test_type === type)
                            .map((test) => test.test_name)
                            .filter(Boolean);
                          return tests.length > 0 ? (
                            <p key={type}>
                              {type === "lab" ? "Lab tests" : "Radiology exams"}: {tests.join(", ")}
                            </p>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="right-col">
                <div className="right-section diagnose-block">
                  <div className="section-label">Diagnose</div>
                  <div className="field-content">{diagnose || "—"}</div>
                </div>
                <div className="right-section prescription-block">
                  <div className="section-label">Prescription</div>
                  <div className="field-content">{description || "—"}</div>
                </div>
              </div>
            </div>

            <p className="confirm-text">
            </p>
            <div className="footer">
              <div className="printed-note">
                Printed on: {new Date().toLocaleDateString()}<br />
                This is a computer-generated document
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrescriptionField({ label, value, onChange, locked, placeholder, rows }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => !locked && onChange(e.target.value)}
        rows={rows}
        readOnly={locked}
        placeholder={locked ? "" : placeholder}
        className={`w-full border rounded-lg p-3 text-sm text-gray-700 focus:outline-none resize-none transition-all ${
          locked
            ? "bg-gray-50 border-gray-100 text-gray-500 cursor-not-allowed"
            : "border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
        }`}
      />
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm text-gray-700 font-medium break-words">{value}</p>
      </div>
    </div>
  );
}

function FeeRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-700">{value}</span>
    </div>
  );
}

function HistoryField({ label, value }) {
  if (!value) return null;
  return (
    <div className="mb-3 last:mb-0">
      <p className="mb-1 text-xs font-medium text-gray-400">{label}</p>
      <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{value}</p>
    </div>
  );
}
